import type { ApolloClient } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import type { GraphQLTaggedNode } from 'relay-runtime';
import type { GraphQLError } from 'graphql';

import type { QueryInteropSupportedArgs } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { isExpectedApolloError } from '@confluence/error-types/entry-points/expectedGraphQlErrorHelper.ts';
import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode.ts';
import {
	writeQueryInterop,
	type WriteQueryInteropArgs,
} from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { fg } from '@confluence/feature-gating';
import { isRelayEnabled } from '@confluence/apollo-relay-interop/entry-points/isRelayEnabled.ts';
import { getQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/queryBoundaryStorage.ts';

import { getOperationName, UNEXPECTED_RESPONSE } from './getOperationName.ts';

import { isUnauthorizedError } from './errors/isUnauthorizedError.ts';
import { isNotFoundError } from './errors/isNotFoundError.ts';
import { isNotFoundSpaceError } from './errors/isNotFoundSpaceError.ts';
import { trackPreloadedQuery } from './developmentQueryBoundaryVerification.ts';
import { getAtlAttributionHeader } from './getAtlAttributionHeader.ts';

type PreloaderGraphQLErrorExtraInfo = {
	operationName: string;
	callerPackage?: string;
};

const addExtraInfoToError = (error: object, extraInfo: PreloaderGraphQLErrorExtraInfo) => {
	try {
		const existingExtraInfo = Reflect.get(error, 'extraInfo');
		const existingExtraInfoObject =
			typeof existingExtraInfo === 'object' && existingExtraInfo !== null
				? existingExtraInfo
				: undefined;

		Reflect.set(error, 'extraInfo', {
			...existingExtraInfoObject,
			...extraInfo,
		});
	} catch {
		// Some error shapes can be non-extensible. Extra info is best-effort diagnostics.
	}
};

const addExtraInfoToGraphQLErrors = (
	errors: ReadonlyArray<GraphQLError> | undefined,
	extraInfo: PreloaderGraphQLErrorExtraInfo,
) => {
	errors?.forEach((error) => {
		addExtraInfoToError(error, extraInfo);
	});
};

const muteExpectedGraphQLErrors = (result: any) => {
	// Preloading is "fire and forget" operation on a client as well, so we can mute
	// some known errors, but we can't really do anything about them as they're not
	// make it to the Apollo cache anyway and therefore don't pop at where they can
	// be handled.
	const hasErrors = result?.errors?.length > 0;
	result?.errors?.forEach((error: any) => {
		if (
			isUnauthorizedError(error) ||
			isExpectedApolloError(error) ||
			isNotFoundSpaceError(error) ||
			isNotFoundError(error)
		) {
			markErrorAsHandled(error);
		}
	});

	return hasErrors;
};

/**
 * Same as Apollo's query function but only used in preloader functions
 */
export function query<Q, V>({
	query: _query,
	variables,
	/**
	 * How to preload with fresh data correctly.
	 * In preloader: query with fetchPolicy: "network-only"
	 */
	fetchPolicy = 'cache-first',
	errorPolicy = 'all',
	context,
	client = window.GLOBAL_APOLLO_CLIENT,
	__callerPackage__,
}: Omit<QueryInteropSupportedArgs<Q, V & Record<string, any>>, 'query'> & {
	query: GraphQLTaggedNode;
	client?: ApolloClient<any>;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
}): Promise<{
	data: Q | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> {
	const query = toDocumentNode(_query);
	const name = getOperationName(query);

	// Detect if this is an agg call by checking if the client is the AGG client
	// The AGG client is created with createWrappedFetch({ isForAggOnly: true })
	const isForAggOnly =
		client === window.GLOBAL_APOLLO_AGG_CLIENT || (client as any)?.__isAGGClient === true;
	const preloaderErrorExtraInfo: PreloaderGraphQLErrorExtraInfo = {
		operationName: name ?? 'unknown',
		callerPackage: __callerPackage__,
	};

	if (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging'
	) {
		const boundaryName = getQueryBoundary();
		if (boundaryName) {
			trackPreloadedQuery(query, variables, boundaryName);
		}
	}

	// This is indirect reference to next/packages/graphql/src/links/DevOnlyGraphqlErrorGenerator.ts
	if (process.env.NODE_ENV !== 'production' && (window as any).graphqlOperationController) {
		const operationController = (window as any).graphqlOperationController;
		const { shouldGenerateError } = operationController.onOperationRequested(name);
		if (shouldGenerateError) {
			return Promise.resolve(UNEXPECTED_RESPONSE);
		}
	}

	const queryContext: Record<string, any> = {
		...context,
	};

	// Only add atl-attribution header if AGG migration feature gate is enabled
	if (fg('cc-agg-migration-use-agg-endpoint')) {
		queryContext.headers = {
			...context?.headers,
			...getAtlAttributionHeader(),
		};
	}

	if (window.__SSR_REACT_STREAMING__) {
		queryContext.preload = true;
		queryContext.originalQuery = query;
	}

	return queryInterop<Q, V & Record<string, any>>({
		query: _query,
		variables: variables as V & Record<string, any>,
		errorPolicy,
		fetchPolicy,
		context: queryContext,
		interopApolloAggClient: isForAggOnly,
		__callerPackage__,
	}).then((result) => {
		const errors = result.error?.graphQLErrors as ReadonlyArray<GraphQLError> | undefined;
		if (result.error) {
			addExtraInfoToError(result.error, preloaderErrorExtraInfo);
		}
		addExtraInfoToGraphQLErrors(errors, preloaderErrorExtraInfo);
		const interopResult = { data: result.data, errors };
		const hasErrors = muteExpectedGraphQLErrors(interopResult);
		return { data: result.data ?? null, errors, hasErrors };
	});
}

export function queryAgg<T, V>(
	options: Omit<QueryInteropSupportedArgs<T, V & Record<string, any>>, 'query'> & {
		query: GraphQLTaggedNode;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	},
): Promise<{
	data: T | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> {
	return query<T, V>({
		...options,
		query: options.query,
		client: window.GLOBAL_APOLLO_AGG_CLIENT,
	});
}

/**
 * @deprecated use writeQueryInterop from `@confluence/apollo-relay-interop/entry-points/writeQuery`
 */
export function writeQuery<T, V extends Record<string, any>>(
	options: WriteQueryInteropArgs<T, V> & {
		client?: ApolloClient<any>;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	},
): void {
	if (isRelayEnabled(options.__callerPackage__)) {
		writeQueryInterop<T, V>({ ...options, __callerPackage__: options.__callerPackage__ });
	} else {
		const client = options.client || window.GLOBAL_APOLLO_CLIENT;
		if (client) {
			client.writeQuery({
				...options,
				query: toDocumentNode(options.query),
			});
		}
	}
}

/**
 * @deprecated use writeQueryInterop from `@confluence/apollo-relay-interop/entry-points/writeQuery`
 */
export function writeAggQuery<T, V extends Record<string, any>>(
	options: WriteQueryInteropArgs<T, V> & {
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	},
): void {
	writeQuery({ ...options, client: window.GLOBAL_APOLLO_AGG_CLIENT });
}
